import { useEffect, useState, useMemo } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Search, Plus, Minus, ShoppingCart } from "lucide-react";
import { isFractional, quantityStep, quantityMin, quantityDefault, normalizeQuantity, unitLabel } from "@/lib/units";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=600";

export default function ShopPage() {
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("Tous");
  const [qty, setQty] = useState({});

  const [promoCategories, setPromoCategories] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes, catFull] = await Promise.all([
          api.get("/products"),
          api.get("/products/categories"),
          api.get("/categories"),
        ]);
        setProducts(pRes.data);
        setCategories(["Tous", ...cRes.data]);
        setPromoCategories(new Set(catFull.data.filter((c) => c.is_promo).map((c) => c.name)));
      } catch (e) {
        toast.error("Impossible de charger le catalogue");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const productCats = (p) => p.categories?.length ? p.categories : (p.category ? [p.category] : []);
  const isPromo = (p) => productCats(p).some((c) => promoCategories.has(c));

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const cats = productCats(p);
      const matchCat = activeCat === "Tous" || cats.includes(activeCat);
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [products, search, activeCat]);

  const getQty = (p) => qty[p.id] ?? quantityDefault(p.unit);
  const setQ = (p, v) => setQty((s) => ({ ...s, [p.id]: v }));
  const step = (p, dir) => {
    const s = quantityStep(p.unit);
    const next = Number(getQty(p)) + dir * s;
    setQ(p, normalizeQuantity(p.unit, next));
  };

  const addToCart = (p) => {
    const q = normalizeQuantity(p.unit, getQty(p));
    cart.add(p, q);
    setQ(p, quantityDefault(p.unit));
    toast.success(`${p.name} ajouté au panier`);
  };

  return (
    <div data-testid="shop-page">
      <Header />
      <section className="ff-container py-10">
        <p className="ff-overline">Catalogue pro</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-3">
          <div>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Notre marché du jour</h1>
            <p className="text-sm text-[#8A9A8E] mt-2">{products.length} produits disponibles · Livraison Genève</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A8E]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit..."
              className="ff-input pl-10 md:w-80"
              data-testid="shop-search-input"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`shop-category-${c}`}
              className={`px-4 py-2 text-sm border transition-colors ${
                activeCat === c
                  ? "bg-[#2D6A4F] text-white border-[#2D6A4F]"
                  : "bg-white border-[#E5E3DC] text-[#4A5D4E] hover:border-[#2D6A4F]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Header row (desktop) */}
        <div className="mt-8 hidden md:grid grid-cols-[80px_2fr_1fr_180px_160px] gap-4 px-4 pb-2 text-xs uppercase tracking-wider text-[#8A9A8E] border-b border-[#E5E3DC]">
          <span></span>
          <span>Produit</span>
          <span className="text-right">Prix</span>
          <span className="text-center">Quantité</span>
          <span></span>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-32 text-center text-[#8A9A8E]">Chargement du catalogue...</div>
        ) : filtered.length === 0 ? (
          <div className="py-32 text-center text-[#8A9A8E]" data-testid="no-products">Aucun produit trouvé.</div>
        ) : (
          <ul className="mt-2 divide-y divide-[#E5E3DC] bg-white border border-[#E5E3DC]" data-testid="product-list">
            {filtered.map((p) => {
              const q = getQty(p);
              const stepVal = quantityStep(p.unit);
              const minVal = quantityMin(p.unit);
              const lineTotal = (Number(q) || 0) * p.price;
              return (
                <li
                  key={p.id}
                  data-testid={`product-row-${p.id}`}
                  className="grid grid-cols-[80px_1fr] md:grid-cols-[80px_2fr_1fr_180px_160px] gap-3 md:gap-4 items-center px-4 py-3 hover:bg-[#F9F8F6] transition-colors"
                >
                  <div className="w-20 h-20 bg-[#F2EFEB] overflow-hidden">
                    <img
                      src={resolveImageUrl(p.image_url) || FALLBACK_IMG}
                      alt={p.name}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {productCats(p).map((c) => (
                        <span key={c} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${promoCategories.has(c) ? "bg-[#E76F51] text-white" : "text-[#8A9A8E]"}`}>
                          {c}
                        </span>
                      ))}
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#F2EFEB] text-[#4A5D4E]">{unitLabel(p.unit)}</span>
                    </div>
                    <h3 className="font-serif text-lg text-[#1A2B1E] mt-0.5 leading-tight truncate">
                      {p.name}
                      {isPromo(p) && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#E76F51]">★ Promo</span>}
                    </h3>
                    {(p.description || p.origin) && (
                      <p className="text-xs text-[#8A9A8E] mt-0.5 line-clamp-1">
                        {p.origin && <span className="text-[#2D6A4F] font-medium">{p.origin}</span>}
                        {p.origin && p.description && " · "}
                        {p.description}
                      </p>
                    )}
                  </div>

                  <div className="text-right hidden md:block">
                    <div className="font-serif text-xl text-[#2D6A4F]" data-testid={`product-price-${p.id}`}>
                      CHF {p.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-[#8A9A8E]">/ {unitLabel(p.unit)}</div>
                  </div>

                  <div className="hidden md:flex items-center justify-center gap-1">
                    <div className="flex items-center border border-[#E5E3DC] bg-white">
                      <button
                        onClick={() => step(p, -1)}
                        className="px-2 py-2 hover:bg-[#F2EFEB] disabled:opacity-40"
                        data-testid={`product-qty-minus-${p.id}`}
                        type="button"
                        disabled={Number(q) <= minVal}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min={minVal}
                        step={stepVal}
                        value={q}
                        onChange={(e) => setQ(p, e.target.value)}
                        onBlur={(e) => setQ(p, normalizeQuantity(p.unit, e.target.value))}
                        className="w-16 text-center text-sm py-2 bg-transparent focus:outline-none"
                        data-testid={`product-qty-input-${p.id}`}
                      />
                      <button
                        onClick={() => step(p, +1)}
                        className="px-2 py-2 hover:bg-[#F2EFEB]"
                        data-testid={`product-qty-plus-${p.id}`}
                        type="button"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="text-xs text-[#8A9A8E] ml-1 w-10 hidden lg:inline">
                      {unitLabel(p.unit)}
                    </span>
                  </div>

                  <div className="flex items-center justify-end gap-2 col-span-2 md:col-span-1">
                    {/* Mobile inline price + qty */}
                    <div className="md:hidden flex items-center gap-2 flex-1">
                      <div className="font-serif text-lg text-[#2D6A4F]">CHF {p.price.toFixed(2)}</div>
                      <div className="flex items-center border border-[#E5E3DC] ml-auto">
                        <button onClick={() => step(p, -1)} type="button" className="px-2 py-1.5"><Minus className="w-3 h-3" /></button>
                        <input
                          type="number"
                          min={minVal}
                          step={stepVal}
                          value={q}
                          onChange={(e) => setQ(p, e.target.value)}
                          onBlur={(e) => setQ(p, normalizeQuantity(p.unit, e.target.value))}
                          className="w-12 text-center text-sm py-1.5 bg-transparent focus:outline-none"
                        />
                        <button onClick={() => step(p, +1)} type="button" className="px-2 py-1.5"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <button
                      onClick={() => addToCart(p)}
                      className="bg-[#2D6A4F] text-white px-3 py-2 text-sm hover:bg-[#1B4332] transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                      data-testid={`product-add-${p.id}`}
                      title={`Ajouter ${q} ${unitLabel(p.unit)} · CHF ${lineTotal.toFixed(2)}`}
                    >
                      <ShoppingCart className="w-4 h-4" /> Ajouter
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <Footer />
    </div>
  );
}
